import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';

import {
  deleteAppExposeBinding,
  listAppExposeBindings,
  listExposeMcpEndpoints,
  upsertAppExposeBinding,
} from '../apps/api';
import { presentAppPickerLabel } from '../apps/leftoverDevHosting';
import ConnectorProbesCard from './ConnectorProbesCard';
import { getDefaultTenantId } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import {
  resetDataSourceLoadError,
  resolveExposeExecuteTenant,
} from './exposeTenantHonesty';

type AppEndpoint = {
  app_id: string;
  name: string;
  app_status?: string;
  mcp_enabled?: boolean;
  hosting_url?: string | null;
  hosting_honesty?: string | null;
  hosting_honesty_label?: string | null;
  hosting_leftover_url?: string | null;
};

type DataSourceBinding = {
  binding_id: string;
  app_id: string;
  source_type: string;
  source_id?: string | null;
  policy_ref?: { kind?: string | null; id?: string | null };
  policy_status?: string | null;
  scopes?: string[];
  filters?: Record<string, unknown>;
  rate_limit?: { rpm?: number | null } | null;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
};

const SOURCE_CATALOG = [
  { type: 'memory', label: 'Memory', description: 'App-scoped living memory recall and storage via MCP and /public/memories.' },
  { type: 'iot_telemetry', label: 'IoT Telemetry', description: 'Read telemetry and analytics from the policy-gated public IoT surface.' },
  { type: 'iot_devices', label: 'IoT Devices', description: 'Read device inventory and health through the public IoT gateway.' },
  { type: 'api_pipeline', label: 'Can read data from its API feeds', description: 'Expose runtime API data paths managed by API Ingest and API Manager.' },
  { type: 'pim', label: 'PIM', description: 'Grant app runtime access to public PIM resources.' },
  { type: 'dam', label: 'DAM', description: 'Grant app runtime access to public DAM resources.' },
  { type: 'entity', label: 'Identity & Entities', description: 'Expose app-scoped entity and identity records.' },
  { type: 'weather', label: 'Weather', description: 'Allow app runtime weather access via the governed public weather route.' },
];

const POLICY_OPTIONS = [
  { value: 'internal_source', label: 'Internal source' },
  { value: 'api_app_connection', label: 'API app connection' },
  { value: 'api_shared_access_grant', label: 'Shared access grant' },
];

function safeParseFilters(value: string) {
  if (!value.trim()) return {};
  return JSON.parse(value);
}

function normalizeFiltersText(filters?: Record<string, unknown>) {
  if (!filters || Object.keys(filters).length === 0) return '';
  return JSON.stringify(filters, null, 2);
}

function formatDateTime(value?: string) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
}

function sourceLabel(sourceType: string) {
  return SOURCE_CATALOG.find((item) => item.type === sourceType)?.label || sourceType;
}

function statusColor(binding?: DataSourceBinding) {
  if (!binding) return 'default';
  return binding.enabled ? 'success' : 'default';
}

export default function DataSourcesTab({ initialAppId = '' }: { initialAppId?: string }) {
  const [apps, setApps] = useState<AppEndpoint[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const sessionTenant = resolveExposeExecuteTenant({
    selectedTenant,
    hostTenant: getDefaultTenantId(),
  });
  const [bindings, setBindings] = useState<DataSourceBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBinding, setEditingBinding] = useState<DataSourceBinding | null>(null);
  const [form, setForm] = useState({
    source_type: 'memory',
    source_id: '',
    policy_kind: 'internal_source',
    policy_id: '',
    scopes: 'read',
    filters: '',
    enabled: true,
    rpm: '',
  });

  const groupedBindings = useMemo(() => {
    const map = new Map<string, DataSourceBinding[]>();
    for (const binding of bindings) {
      if (!map.has(binding.source_type)) map.set(binding.source_type, []);
      map.get(binding.source_type)!.push(binding);
    }
    return map;
  }, [bindings]);

  const stats = useMemo(() => {
    const enabled = bindings.filter((binding) => binding.enabled).length;
    const disabled = bindings.filter((binding) => !binding.enabled).length;
    const memoryEnabled = bindings.filter((binding) => binding.source_type === 'memory' && binding.enabled).length;
    const rateLimited = bindings.filter((binding) => Number(binding.rate_limit?.rpm || 0) > 0).length;
    return { enabled, disabled, memoryEnabled, rateLimited };
  }, [bindings]);

  async function loadApps() {
    const response = await listExposeMcpEndpoints(sessionTenant);
    const endpoints = response.endpoints || [];
    setApps(endpoints);
    setSelectedAppId((current) => current || initialAppId || endpoints[0]?.app_id || '');
  }

  async function loadBindings(appId: string) {
    if (!appId) {
      setBindings([]);
      return;
    }
    const response = await listAppExposeBindings(appId, sessionTenant);
    setBindings(response.bindings || []);
  }

  async function refreshAll(appIdOverride?: string) {
    const appId = appIdOverride ?? selectedAppId;
    try {
      setLoading(true);
      setError(null);
      await loadApps();
      await loadBindings(appId);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data sources');
      setBindings([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialAppId) {
      setSelectedAppId(initialAppId);
    }
  }, [initialAppId]);

  useEffect(() => {
    setError(resetDataSourceLoadError());
    if (!selectedAppId) {
      setBindings([]);
      return;
    }
    loadBindings(selectedAppId).catch((err: any) => {
      setError(err?.message || 'Failed to load data sources');
      setBindings([]);
    });
  }, [selectedAppId, sessionTenant]);

  function openCreateDialog(sourceType: string) {
    setEditingBinding(null);
    setForm({
      source_type: sourceType,
      source_id: '',
      policy_kind: 'internal_source',
      policy_id: '',
      scopes: 'read',
      filters: '',
      enabled: true,
      rpm: '',
    });
    setDialogOpen(true);
  }

  function openEditDialog(binding: DataSourceBinding) {
    setEditingBinding(binding);
    setForm({
      source_type: binding.source_type,
      source_id: binding.source_id || '',
      policy_kind: binding.policy_ref?.kind || 'internal_source',
      policy_id: binding.policy_ref?.id || '',
      scopes: Array.isArray(binding.scopes) && binding.scopes.length > 0 ? binding.scopes.join(', ') : 'read',
      filters: normalizeFiltersText(binding.filters),
      enabled: Boolean(binding.enabled),
      rpm: binding.rate_limit?.rpm ? String(binding.rate_limit.rpm) : '',
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!selectedAppId) return;
    try {
      setSaving(true);
      setError(null);
      const scopes = form.scopes.split(',').map((value) => value.trim()).filter(Boolean);
      const filters = safeParseFilters(form.filters);
      await upsertAppExposeBinding(selectedAppId, sessionTenant, {
        source_type: form.source_type,
        source_id: form.source_id || null,
        policy_ref: {
          kind: form.policy_kind || null,
          id: form.policy_id || null,
        },
        scopes,
        filters,
        enabled: form.enabled,
        rate_limit: form.rpm ? { rpm: Number(form.rpm) } : null,
      });
      setDialogOpen(false);
      await loadBindings(selectedAppId);
    } catch (err: any) {
      setError(err?.message || 'Failed to save data source');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(binding: DataSourceBinding, enabled: boolean) {
    if (!selectedAppId) return;
    try {
      setError(null);
      await upsertAppExposeBinding(selectedAppId, sessionTenant, {
        source_type: binding.source_type,
        source_id: binding.source_id || null,
        policy_ref: binding.policy_ref || { kind: 'internal_source', id: null },
        scopes: binding.scopes || ['read'],
        filters: binding.filters || {},
        enabled,
        rate_limit: binding.rate_limit || null,
      });
      await loadBindings(selectedAppId);
    } catch (err: any) {
      setError(err?.message || 'Failed to update data source');
    }
  }

  async function handleDelete(binding: DataSourceBinding) {
    if (!selectedAppId) return;
    if (!window.confirm(`Delete ${sourceLabel(binding.source_type)} binding${binding.source_id ? ` (${binding.source_id})` : ''}?`)) return;
    try {
      setError(null);
      await deleteAppExposeBinding(selectedAppId, sessionTenant, binding.binding_id);
      await loadBindings(selectedAppId);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete data source');
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6">Data Sources</Typography>
          <Typography variant="body2" color="text.secondary">
            Runtime bindings from the same connection door as <code>dc__get_data_connection</code>
            {sessionTenant ? ` (tenant ${sessionTenant})` : ''} — not the leftover expose-agent list.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="data-source-app-label">App Instance</InputLabel>
            <Select
              labelId="data-source-app-label"
              label="App Instance"
              value={selectedAppId}
              onChange={(event) => setSelectedAppId(String(event.target.value))}
            >
              {apps.map((app) => (
                <MenuItem key={app.app_id} value={app.app_id}>
                  {presentAppPickerLabel({
                    name: app.name,
                    app_id: app.app_id,
                    status: app.app_status,
                    tenantId: selectedTenant,
                    hosting_url: app.hosting_url,
                    hosting_leftover_url: app.hosting_leftover_url,
                    hosting_honesty: app.hosting_honesty,
                    hosting_honesty_label: app.hosting_honesty_label,
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refreshAll(selectedAppId)} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !selectedAppId ? (
        <Alert severity="info">No App Instances available yet. Create an app first, then come back to configure data sources.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: 'Enabled', value: stats.enabled },
              { label: 'Disabled', value: stats.disabled },
              { label: 'Memory Ready', value: stats.memoryEnabled },
              { label: 'Rate Limited', value: stats.rateLimited },
            ].map((item) => (
              <Card key={item.label} variant="outlined">
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{item.label}</Typography>
                  <Typography variant="h5">{item.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
            {SOURCE_CATALOG.map((source) => {
              const sourceBindings = groupedBindings.get(source.type) || [];
              return (
                <Card key={source.type} variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6">{source.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {source.description}
                        </Typography>
                      </Box>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateDialog(source.type)}>
                        Add
                      </Button>
                    </Stack>

                    {sourceBindings.length === 0 ? (
                      <Alert severity="info" variant="outlined">
                        Not configured for this app yet.
                      </Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        {sourceBindings.map((binding) => (
                          <Box key={binding.binding_id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                                  <Chip size="small" label={binding.source_id || 'default'} color={statusColor(binding) as any} />
                                  <Chip size="small" variant="outlined" label={binding.policy_status || binding.policy_ref?.kind || 'internal_source'} />
                                  {(binding.scopes || []).map((scope) => (
                                    <Chip key={`${binding.binding_id}-${scope}`} size="small" variant="outlined" label={scope} />
                                  ))}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Updated {formatDateTime(binding.updated_at)}
                                  {binding.rate_limit?.rpm ? ` | ${binding.rate_limit.rpm} rpm` : ''}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={binding.enabled ? <ToggleOffIcon /> : <ToggleOnIcon />}
                                  onClick={() => handleToggle(binding, !binding.enabled)}
                                >
                                  {binding.enabled ? 'Disable' : 'Enable'}
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(binding)}>
                                  Edit
                                </Button>
                                <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDelete(binding)}>
                                  Delete
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <ConnectorProbesCard
            key={selectedAppId}
            appId={selectedAppId}
            tenantId={sessionTenant}
            onChanged={() => loadBindings(selectedAppId)}
          />
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBinding ? 'Edit Data Source Binding' : 'Add Data Source Binding'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="data-source-type-label">Source Type</InputLabel>
              <Select
                labelId="data-source-type-label"
                label="Source Type"
                value={form.source_type}
                onChange={(event) => setForm((current) => ({ ...current, source_type: String(event.target.value) }))}
              >
                {SOURCE_CATALOG.map((source) => (
                  <MenuItem key={source.type} value={source.type}>{source.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Source ID"
              size="small"
              value={form.source_id}
              onChange={(event) => setForm((current) => ({ ...current, source_id: event.target.value }))}
              helperText="Optional. Use this for source-specific bindings such as a named pipeline or API."
            />
            <FormControl fullWidth size="small">
              <InputLabel id="policy-kind-label">Policy Source</InputLabel>
              <Select
                labelId="policy-kind-label"
                label="Policy Source"
                value={form.policy_kind}
                onChange={(event) => setForm((current) => ({ ...current, policy_kind: String(event.target.value) }))}
              >
                {POLICY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Policy Reference ID"
              size="small"
              value={form.policy_id}
              onChange={(event) => setForm((current) => ({ ...current, policy_id: event.target.value }))}
              helperText="Connection ID for BYOK or API ID for shared-access grants."
            />
            <TextField
              label="Scopes"
              size="small"
              value={form.scopes}
              onChange={(event) => setForm((current) => ({ ...current, scopes: event.target.value }))}
              helperText="Comma-separated values like read, write"
            />
            <TextField
              label="Filters (JSON)"
              size="small"
              multiline
              minRows={4}
              value={form.filters}
              onChange={(event) => setForm((current) => ({ ...current, filters: event.target.value }))}
              helperText='Optional JSON, for example {"device_ids":["device-1"]}'
            />
            <TextField
              label="Rate limit RPM"
              size="small"
              type="number"
              value={form.rpm}
              onChange={(event) => setForm((current) => ({ ...current, rpm: event.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="data-source-enabled-label">Status</InputLabel>
              <Select
                labelId="data-source-enabled-label"
                label="Status"
                value={form.enabled ? 'enabled' : 'disabled'}
                onChange={(event) => setForm((current) => ({ ...current, enabled: String(event.target.value) === 'enabled' }))}
              >
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !selectedAppId}>
            {saving ? 'Saving...' : 'Save Binding'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
